import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import {
  MessageSquare, Facebook, Instagram, Check, Loader2, Plug, ShieldCheck, Trash2, Info, ArrowRight
} from "lucide-react";

const CHANNELS = {
  whatsapp: { label: "WhatsApp Business", icon: MessageSquare, color: "text-emerald-600 bg-emerald-100" },
  facebook: { label: "Facebook Page", icon: Facebook, color: "text-blue-600 bg-blue-100" },
  instagram: { label: "Instagram Business", icon: Instagram, color: "text-pink-600 bg-pink-100" },
};

const WIZARD_STEPS = [
  "Login dengan Meta",
  "Pilih Business Portfolio",
  "Pilih WhatsApp Account + Nombor",
  "Pilih Facebook Page",
  "Pilih Instagram Business",
  "Authorize & Sambung",
];

export default function Connect() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [running, setRunning] = useState(false);
  const { data: connections = [] } = useQuery({ queryKey: ["connections"], queryFn: () => api.get("/connections").then((r) => r.data) });

  const runWizard = async () => {
    setRunning(true);
    for (let i = 0; i < WIZARD_STEPS.length; i++) {
      setStep(i);
      await new Promise((r) => setTimeout(r, 850));
    }
    try {
      await api.post("/connections/connect", { channels: ["whatsapp", "facebook", "instagram"] });
      qc.invalidateQueries({ queryKey: ["connections"] });
      qc.invalidateQueries({ queryKey: ["meta-center"] });
      toast.success("Channel berjaya disambung!");
    } catch { toast.error("Sambungan gagal"); }
    setRunning(false);
    setOpen(false);
    setStep(0);
  };

  const disconnect = async (id) => {
    await api.delete(`/connections/${id}`);
    qc.invalidateQueries({ queryKey: ["connections"] });
    qc.invalidateQueries({ queryKey: ["meta-center"] });
    toast.success("Channel diputuskan");
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="connect-page">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Plug className="h-6 w-6 text-primary" /> Connect Your Business</h1>
        <p className="text-muted-foreground text-sm mt-1">Sambung aset Meta anda sendiri — WhatsApp, Facebook & Instagram — ke Unified Inbox WasapMe.</p>
      </div>

      <div className="rounded-xl border border-sky-200 bg-sky-50 dark:bg-sky-950/20 dark:border-sky-900/40 p-4 text-sm text-sky-800 dark:text-sky-300 flex gap-3">
        <Info className="h-5 w-5 shrink-0 mt-0.5" />
        <p><span className="font-semibold">Mod DEMO:</span> Sambungan disimulasikan buat masa ini. Untuk LIVE, WasapMe perlu Meta App ID, App Secret, Config ID (Embedded Signup v4) & lulus Meta App Review. Selepas itu butang di bawah akan buka Meta OAuth sebenar.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <div className="flex justify-center gap-4 mb-5">
          {Object.entries(CHANNELS).map(([k, c]) => (
            <div key={k} className={`h-14 w-14 rounded-2xl flex items-center justify-center ${c.color}`}><c.icon className="h-7 w-7" /></div>
          ))}
        </div>
        <h2 className="font-display text-xl font-bold">Connect Your Business</h2>
        <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">Satu klik untuk sambung WhatsApp, Facebook Page & Instagram. Tak perlu faham App ID atau webhook.</p>
        <Button onClick={() => setOpen(true)} data-testid="connect-with-meta-button"
          className="mt-6 rounded-full bg-[#1877F2] hover:bg-[#1568d8] text-white h-12 px-8 text-base">
          <Facebook className="h-5 w-5 mr-2" /> Connect with Meta
        </Button>
      </div>

      <div>
        <h3 className="font-display font-semibold mb-3">Channel Disambung ({connections.length})</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {connections.map((con) => {
            const c = CHANNELS[con.channel] || CHANNELS.whatsapp;
            return (
              <div key={con.id} data-testid={`connection-card-${con.id}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${c.color}`}><c.icon className="h-5 w-5" /></div>
                    <div>
                      <p className="font-semibold text-sm">{con.name}</p>
                      <p className="text-xs text-muted-foreground">{con.identifier}</p>
                    </div>
                  </div>
                  <button onClick={() => disconnect(con.id)} data-testid={`disconnect-${con.id}`} className="text-muted-foreground hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <Badge className="bg-emerald-100 text-emerald-700 border-0 rounded-full text-[10px]"><Check className="h-3 w-3 mr-0.5" /> Connected</Badge>
                  {con.channel === "whatsapp" && <Badge className="bg-emerald-100 text-emerald-700 border-0 rounded-full text-[10px]">Quality {con.quality}</Badge>}
                  {con.mode === "demo" && <Badge variant="outline" className="rounded-full text-[10px]">Demo</Badge>}
                </div>
              </div>
            );
          })}
          {connections.length === 0 && <p className="text-sm text-muted-foreground">Belum ada channel. Klik "Connect with Meta".</p>}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => !running && setOpen(v)}>
        <DialogContent data-testid="meta-wizard-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Facebook className="h-5 w-5 text-[#1877F2]" /> Meta Authorization</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            {WIZARD_STEPS.map((s, i) => (
              <div key={s} className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${running && step === i ? "bg-accent" : ""}`}>
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs shrink-0 ${
                  running && step > i ? "bg-emerald-500 text-white" : running && step === i ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                  {running && step > i ? <Check className="h-4 w-4" /> : running && step === i ? <Loader2 className="h-4 w-4 animate-spin" /> : i + 1}
                </div>
                <span className={`text-sm ${running && step >= i ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s}</span>
              </div>
            ))}
          </div>
          {!running && (
            <Button onClick={runWizard} data-testid="meta-wizard-authorize" className="rounded-full w-full bg-[#1877F2] hover:bg-[#1568d8] text-white">
              Authorize <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Mod demo — tiada panggilan Meta sebenar sehingga kredential dimasukkan.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
